import React from 'react';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import moment from 'moment';

import Modal from '../../../common/components/organisms/Modal';
import { getAnnotationColor, getAnnotationIcon } from '../utils/annotationPresentation';
import {
  ModalContent,
  HeaderSection,
  IconCircle,
  HeaderText,
  Title,
  Subtitle,
  ContentSection,
  SectionTitle,
  Description,
  TaskSpecificSection,
  TaskField,
  TaskLabel,
  TaskValue,
} from './TaskAnnotationModal.styled';

function formatAnnotationType(type) {
  return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

function getDisplay(value, fallback = i18n.t('common.na')) {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'object') {
    if (value.text) return value.text;
    if (value.name) return value.name;
    if (value.label) return value.label;
    // Show id or JSON string as last resort
    if (value.id) return String(value.id);
    try { return JSON.stringify(value); } catch (error) { return fallback; }
  }
  return String(value);
}

const TaskAnnotationModal = ({ annotation, onClose }) => {
  const typeColor = getAnnotationColor(annotation, { respectSeverity: false });
  const typeIcon = getAnnotationIcon(annotation.type);
  const baseMetadata = annotation.base_metadata || {};

  const fields = [
    { label: i18n.t('taskDetailsModal.fields.recommendedAction'), value: baseMetadata.recommended_action },
    { label: i18n.t('common.state'), value: baseMetadata.state },
    { label: i18n.t('taskDetailsModal.fields.componentType'), value: baseMetadata.component_type || baseMetadata.component_subtype },
    { label: i18n.t('taskDetailsModal.fields.taskType'), value: baseMetadata.task_type },
    { label: i18n.t('taskDetailsModal.fields.condition'), value: baseMetadata.condition },
  ];

  // Compute a strict defect display: only when both name and type are present
  const defectDisplay = (() => {
    const d = baseMetadata && baseMetadata.defect;
    if (d && typeof d === 'object' && d.name && d.type) {
      return `${d.name} (${d.type})`;
    }
    return null;
  })();

  return (
    <Modal
      title={i18n.t('annotations.taskAnnotationModal.title', { type: formatAnnotationType(annotation.type) })}
      close={onClose}
      footer={() => (<button onClick={onClose}>{i18n.t('common.close')}</button>)}
    >
      <ModalContent>
        <HeaderSection>
          <IconCircle bg={typeColor}>
            <span aria-hidden="true">{typeIcon}</span>
          </IconCircle>
          <HeaderText>
            <Title>{formatAnnotationType(annotation.type)}</Title>
            <Subtitle>
              {i18n.t('annotations.taskAnnotationModal.createdBy', {
                createdAt: moment(annotation.created_at).format('MMM DD, YYYY HH:mm'),
                createdBy: annotation.created_by_name || annotation.created_by
              })}
            </Subtitle>
          </HeaderText>
        </HeaderSection>

        <ContentSection>
          <SectionTitle>{i18n.t('annotations.taskAnnotationModal.sections.description')}</SectionTitle>
          <Description>
            {annotation.description || i18n.t('annotations.taskAnnotationModal.noDescription')}
          </Description>
        </ContentSection>

        <TaskSpecificSection>
          <SectionTitle>{i18n.t('annotations.taskAnnotationModal.sections.taskInformation')}</SectionTitle>

          {baseMetadata.task_name && (
            <TaskField>
              <TaskLabel>{i18n.t('annotations.taskAnnotationModal.fields.taskName')}</TaskLabel>
              <TaskValue>{getDisplay(baseMetadata.task_name)}</TaskValue>
            </TaskField>
          )}

          {fields.map(({ label, value }) => (
            <TaskField key={label}>
              <TaskLabel>{label}</TaskLabel>
              <TaskValue>{getDisplay(value)}</TaskValue>
            </TaskField>
          ))}

          {defectDisplay && (
            <TaskField>
              <TaskLabel>{i18n.t('annotations.taskAnnotationModal.fields.defect')}</TaskLabel>
              <TaskValue>{defectDisplay}</TaskValue>
            </TaskField>
          )}

          {baseMetadata.user_role_assigned && (
            <TaskField>
              <TaskLabel>{i18n.t('annotations.taskAnnotationModal.fields.assignedRole')}</TaskLabel>
              <TaskValue>{getDisplay(baseMetadata.user_role_assigned)}</TaskValue>
            </TaskField>
          )}
        </TaskSpecificSection>
      </ModalContent>
    </Modal>
  );
};

TaskAnnotationModal.propTypes = {
  annotation: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.string.isRequired,
    start_time: PropTypes.string.isRequired,
    end_time: PropTypes.string,
    description: PropTypes.string,
    severity: PropTypes.oneOf(['low', 'medium', 'high', 'critical']),
    created_by: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    created_by_name: PropTypes.string,
    created_at: PropTypes.string,
    source_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    source_type: PropTypes.string,
    base_metadata: PropTypes.object,
    user_metadata: PropTypes.object
  }).isRequired,
  onClose: PropTypes.func.isRequired
};

export default TaskAnnotationModal;


